import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import resolveMediaUrl from '@/utils/resolveMediaUrl';
import {
  Building2, ArrowUp, ArrowDown, Save, Eye, EyeOff, GripVertical,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomepageOrder = () => {
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('homepage'); // 'homepage' | 'all'

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      const sorted = res.data.sort((a, b) => {
        if (a.show_on_homepage && !b.show_on_homepage) return -1;
        if (!a.show_on_homepage && b.show_on_homepage) return 1;
        const posA = a.homepage_position ?? 9999;
        const posB = b.homepage_position ?? 9999;
        return posA - posB;
      });
      setAllProjects(sorted);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const toggleHomepage = (projectId) => {
    setAllProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;
      const newShow = !p.show_on_homepage;
      return { ...p, show_on_homepage: newShow, homepage_position: newShow ? (p.homepage_position || getNextPosition()) : null };
    }));
  };

  const getNextPosition = () => {
    const positions = allProjects.filter(p => p.show_on_homepage && p.homepage_position).map(p => p.homepage_position);
    return positions.length > 0 ? Math.max(...positions) + 1 : 1;
  };

  const setPosition = (projectId, pos) => {
    setAllProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, homepage_position: pos === '' ? null : parseInt(pos) || null } : p
    ));
  };

  const moveUp = (projectId) => {
    const homepage = allProjects.filter(p => p.show_on_homepage).sort((a, b) => (a.homepage_position ?? 9999) - (b.homepage_position ?? 9999));
    const idx = homepage.findIndex(p => p.id === projectId);
    if (idx <= 0) return;
    const current = homepage[idx];
    const above = homepage[idx - 1];
    setAllProjects(prev => prev.map(p => {
      if (p.id === current.id) return { ...p, homepage_position: above.homepage_position };
      if (p.id === above.id) return { ...p, homepage_position: current.homepage_position };
      return p;
    }));
  };

  const moveDown = (projectId) => {
    const homepage = allProjects.filter(p => p.show_on_homepage).sort((a, b) => (a.homepage_position ?? 9999) - (b.homepage_position ?? 9999));
    const idx = homepage.findIndex(p => p.id === projectId);
    if (idx < 0 || idx >= homepage.length - 1) return;
    const current = homepage[idx];
    const below = homepage[idx + 1];
    setAllProjects(prev => prev.map(p => {
      if (p.id === current.id) return { ...p, homepage_position: below.homepage_position };
      if (p.id === below.id) return { ...p, homepage_position: current.homepage_position };
      return p;
    }));
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const updates = allProjects.map(p => ({
        project_id: p.id,
        show_on_homepage: p.show_on_homepage || false,
        homepage_position: p.show_on_homepage ? p.homepage_position : null,
      }));
      await axios.put(`${API}/projects/homepage-order`, updates, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Homepage order saved!');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  const homepageProjects = allProjects.filter(p => p.show_on_homepage).sort((a, b) => (a.homepage_position ?? 9999) - (b.homepage_position ?? 9999));
  const otherProjects = allProjects.filter(p => !p.show_on_homepage);
  const displayProjects = filter === 'homepage' ? homepageProjects : allProjects;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#c9a84c] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="homepage-order">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Homepage Project Order</h1>
          <p className="text-sm text-[#8A8A85] mt-1">
            {homepageProjects.length} projects on homepage · {allProjects.length} total
          </p>
        </div>
        <Button
          onClick={saveOrder}
          disabled={saving}
          className="text-white"
          style={{ background: 'linear-gradient(135deg, #8a6a10, #c9a84c)' }}
          data-testid="save-order-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Order'}
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setFilter('homepage')}
          className="px-4 py-2 rounded-lg text-xs uppercase tracking-wider font-semibold transition-all"
          style={{
            background: filter === 'homepage' ? 'linear-gradient(135deg, #8a6a10, #c9a84c)' : 'transparent',
            color: filter === 'homepage' ? '#fff' : '#8a6a10',
            border: filter === 'homepage' ? 'none' : '1px solid rgba(201,168,76,0.3)',
          }}
        >
          <Eye className="w-3 h-3 inline mr-1" /> On Homepage ({homepageProjects.length})
        </button>
        <button
          onClick={() => setFilter('all')}
          className="px-4 py-2 rounded-lg text-xs uppercase tracking-wider font-semibold transition-all"
          style={{
            background: filter === 'all' ? 'linear-gradient(135deg, #8a6a10, #c9a84c)' : 'transparent',
            color: filter === 'all' ? '#fff' : '#8a6a10',
            border: filter === 'all' ? 'none' : '1px solid rgba(201,168,76,0.3)',
          }}
        >
          All Projects ({allProjects.length})
        </button>
      </div>

      {/* Project list */}
      <div className="space-y-2">
        {displayProjects.map((project, idx) => (
          <div
            key={project.id}
            className="flex items-center gap-4 p-4 rounded-xl transition-all"
            style={{
              background: project.show_on_homepage ? '#fff' : '#fdf9f0',
              border: `1px solid ${project.show_on_homepage ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.1)'}`,
              opacity: project.show_on_homepage ? 1 : 0.7,
            }}
            data-testid={`order-row-${project.id}`}
          >
            {/* Position number */}
            {project.show_on_homepage && (
              <div className="flex items-center gap-1">
                <div className="flex flex-col">
                  <button onClick={() => moveUp(project.id)} className="p-0.5 hover:text-[#8a6a10] text-[#c9a84c] transition-colors">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => moveDown(project.id)} className="p-0.5 hover:text-[#8a6a10] text-[#c9a84c] transition-colors">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <input
                  type="number"
                  min="1"
                  value={project.homepage_position || ''}
                  onChange={(e) => setPosition(project.id, e.target.value)}
                  className="w-14 h-9 text-center text-sm font-bold rounded-lg border focus:outline-none focus:ring-1"
                  style={{ borderColor: 'rgba(201,168,76,0.3)', color: '#8a6a10', focusRingColor: '#c9a84c' }}
                  data-testid={`position-input-${project.id}`}
                />
              </div>
            )}
            {!project.show_on_homepage && <div className="w-[72px]" />}

            {/* Thumbnail */}
            {project.thumbnail ? (
              <img src={resolveMediaUrl(project.thumbnail)} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#f5ead8' }}>
                <Building2 className="w-5 h-5" style={{ color: 'rgba(201,168,76,0.4)' }} />
              </div>
            )}

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#1A1A1A] truncate">{project.project_name}</p>
              <p className="text-xs text-[#8A8A85] truncate">{project.developer_name} · {project.sector} · {project.project_type}</p>
            </div>

            {/* Status badge */}
            <span className="text-[9px] px-2 py-1 rounded-full font-semibold uppercase tracking-wider flex-shrink-0 hidden sm:block"
              style={{ background: '#fdf4e0', color: '#8a6a10' }}>
              {project.project_status}
            </span>

            {/* Toggle */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[10px] uppercase tracking-wider hidden sm:block" style={{ color: project.show_on_homepage ? '#8a6a10' : '#8A8A85', fontFamily: "'Josefin Sans', sans-serif" }}>
                {project.show_on_homepage ? 'Visible' : 'Hidden'}
              </span>
              <Switch
                checked={project.show_on_homepage || false}
                onCheckedChange={() => toggleHomepage(project.id)}
                data-testid={`toggle-${project.id}`}
              />
            </div>
          </div>
        ))}
      </div>

      {filter === 'homepage' && homepageProjects.length === 0 && (
        <div className="text-center py-16 rounded-xl" style={{ background: '#fff', border: '1px solid rgba(201,168,76,0.1)' }}>
          <EyeOff className="w-12 h-12 mx-auto mb-4" style={{ color: 'rgba(201,168,76,0.3)' }} />
          <p className="text-[#8A8A85]">No projects on homepage yet.</p>
          <p className="text-xs text-[#8A8A85] mt-1">Switch to "All Projects" tab and toggle projects to show on homepage.</p>
        </div>
      )}

      {/* Save button at bottom */}
      {displayProjects.length > 10 && (
        <div className="mt-6 text-center">
          <Button
            onClick={saveOrder}
            disabled={saving}
            className="text-white px-8"
            style={{ background: 'linear-gradient(135deg, #8a6a10, #c9a84c)' }}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Order'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default HomepageOrder;
